import React, { useMemo } from 'react';
import { ToastContainer } from 'react-toastify';
import Loader from 'src/components/Loader';
import Logo from 'src/components/Logo/Logo';
import Timeline from 'src/components/Timeline/Timeline';
import {
  useMagicLinkServiceRequestAPI,
  UseMagicLinkServiceRequestAPIReturn,
} from './CustomHooks/useMagicLinkServiceRequestAPI';
import { buildOfflineUser, decodeTokenPayload, OfflineUser } from './helpers';
import ModernizedServiceRequestHeader from './components/ModernizedServiceRequestHeader/ModernizedServiceRequestHeader';
import Callout from 'src/components/InformationCallout/Callout';
import MagicLinkServiceRequestDetails from 'src/pages/MagicLinkSignedOutServiceRequest/components/MagicLinkServiceRequestDetails/MagicLinkServiceRequestDetails';
import ModernizedServiceRequestActions from './components/ModernizedServiceRequestActions/ModernizedServiceRequestActions';
import ModernizedServiceRequestTabs from './components/ModernizedServiceRequestTabs/ModernizedServiceRequestTabs';
import './MagicLinkSignedOutServiceRequest.scss';

interface MagicLinkSignedOutSRProps {
  match: {
    params: {
      id: string;
    };
  };
}

const MagicLinkSignedOutContent: React.FC<{
  offlineUser: OfflineUser;
  serviceRequest: any;
  activity: any[];
  loading: boolean;
  error: string | null;
  documents: any[];
  messages: any[];
  statuses: any[];
  selectedStatusId: string;
  updateStatus: (id: string) => void;
  saveDocument: (formData: any, srId: string, userId: string) => Promise<void>;
  postMessageToAPI: (chat: any) => Promise<void>;
  homeOwnerDataVisible: boolean | null;
  saving: boolean;
  token: string;
}> = ({
  offlineUser,
  serviceRequest,
  activity,
  loading,
  error,
  documents,
  messages,
  statuses,
  selectedStatusId,
  updateStatus,
  saveDocument,
  postMessageToAPI,
  homeOwnerDataVisible,
  saving,
  token,
}) => {
  if (loading && !serviceRequest) {
    return <Loader center={true} />;
  }

  if (error) {
    return (
      <div className="offline-sr-error-container">
        <div className="offline-sr-error-card">
          <Logo />
          <h2>Unable to load service request</h2>
          <p>This link may have expired or is no longer valid.</p>
        </div>
      </div>
    );
  }

  if (!serviceRequest) return null;

  return (
    <div className="offline-sr-container">
      <ToastContainer style={{ width: 'min(90vw, 380px)' }} />
      <div
        className={`offline-sr-card${saving ? ' offline-sr-card-saving' : ''}`}
      >
        <ModernizedServiceRequestHeader
          callout={
            <Callout
              variant="info"
              title="You're viewing this request without signing in"
              subtitle={
                <>
                  You can update the status, leave notes, view messages, and
                  access photos and documents directly from this page.{' '}
                  <strong>Do not share or forward this link</strong> — anyone
                  with it can view and update this request without a password.
                </>
              }
            />
          }
          details={
            <MagicLinkServiceRequestDetails
              serviceRequest={serviceRequest}
              ticketNumber={
                serviceRequest.ticketNumber ?? serviceRequest.ticketId
              }
              homeOwnerDataVisible={homeOwnerDataVisible}
            />
          }
        />
        <div className="offline-sr-body">
          <div className="offline-sr-body-timeline">
            <div className="offline-sr-body-timeline-inner">
              <Timeline activity={activity} loading={false} />
            </div>
          </div>
          <div className="offline-sr-body-main">
            <ModernizedServiceRequestActions
              statuses={statuses}
              selectedStatusId={selectedStatusId}
              currentStatusName={serviceRequest.status?.name ?? ''}
              assignedToName={serviceRequest.serviceProvider?.name}
              saving={saving}
              onStatusChange={updateStatus}
            />
            <ModernizedServiceRequestTabs
              userId={offlineUser.id}
              saving={saving}
              user={offlineUser}
              token={token}
              serviceRequest={serviceRequest}
              documents={documents}
              messages={messages}
              saveDocument={saveDocument}
              postMessageToAPI={postMessageToAPI}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const MagicLinkSignedOutServiceRequest = ({
  match,
}: MagicLinkSignedOutSRProps) => {
  const serviceRequestId: string = match.params.id;
  const params = new URLSearchParams(window.location.search);
  const token: string = params.get('token') ?? '';
  const userId: string = params.get('userId') ?? '';

  const offlineUser: OfflineUser = useMemo((): OfflineUser => {
    const tokenPayload: any = decodeTokenPayload(token);

    return buildOfflineUser(
      userId,
      tokenPayload?.firstName ?? '',
      tokenPayload?.lastName ?? ''
    );
  }, [token, userId]);

  const offlineAPI: UseMagicLinkServiceRequestAPIReturn =
    useMagicLinkServiceRequestAPI(serviceRequestId, token, userId);

  return (
    <MagicLinkSignedOutContent
      offlineUser={offlineUser}
      serviceRequest={offlineAPI.serviceRequest}
      activity={offlineAPI.activity}
      loading={offlineAPI.loading}
      error={offlineAPI.error}
      documents={offlineAPI.documents}
      messages={offlineAPI.messages}
      statuses={offlineAPI.statuses}
      selectedStatusId={offlineAPI.selectedStatusId}
      updateStatus={offlineAPI.updateStatus}
      saveDocument={offlineAPI.saveDocument}
      postMessageToAPI={offlineAPI.postMessageToAPI}
      homeOwnerDataVisible={offlineAPI.homeOwnerDataVisible}
      saving={offlineAPI.saving}
      token={token}
    />
  );
};

export default MagicLinkSignedOutServiceRequest;
